import React, { useCallback, useEffect, useMemo } from 'react';
import { Formik, FormikProps } from 'formik';
import { Col, Row } from 'antd';
import { Form, Input, Select } from 'formik-antd';
import * as Yup from 'yup';
import cx from 'classnames';
import moment from 'moment';

import Radio from 'components/formik/Radio';
import FormikEffect from 'components/formik/FormikEffect';
import FormItem from 'components/formik/FormItem';
import DatePicker from 'components/formik/DatePicker';
import {
  getAnticipateOtherHoursOptions,
  getAnticipateScheduleOptions,
  getDayTimeOptions,
  getEmploymentCategoryOptions,
  getEmploymentTypeOptions,
  getPreScreeningBilledToOptions,
  getWorkFromHomeOptions,
} from 'utils/assignmentUtils';
import {
  isJobTitlesLoadingSelector,
  jobTitlesSelector,
} from 'redux/selectors/assignmentSelectors';
import { getJobTitles } from 'redux/reducers/assignmentSlice';
import {
  AnticipatedScheduleTypeEnum,
  AssignmentRateTypeEnum,
  AssignmentStatusTypeEnum,
  AssignmentTeammateSelectModel,
  AssignmentTeammateTypeEnum,
  CustomerResponseModel,
  EmploymentTypeEnum,
  JobCategoryEnum,
  PreScreeningBilledToEnum,
  PriceAgreementW2InfoResponseModel,
  ResourceDownloadResponse,
  TimeAMPMEnum,
  TimePointEnum,
  WorkFromHomeEnum,
} from 'api/backofficeApiClient';
import InternalReportFieldArray from './InternalReportFieldArray';
import { disableDatesBeforeSpecificDate, getLaterDate } from 'utils/dateUtils';
import { validateNoEndDateLessStartDate } from 'utils/validationUtils';
import FormikAutoCompleteWithLoading from 'hocs/withLoading/FormikAutoCompleteWithLoading';
import {
  initialInternalReportValidationSchema,
  submitInternalReportValidationSchema,
} from './InternalReportFieldArray/InternalReportSectionArray/InternalReportSection';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import {
  isAdminSelector,
  isCorporateTASelector,
} from 'redux/selectors/authenticationSelectors';
import FormikTextEditor, {
  FormikTextEditorValue,
} from 'components/formik/FormikTextEditor';
import { getCustomerBreakInServiceValue } from 'utils/clientUtils';
import SupportDocumentUploader from './SupportDocumentUploader';
import { BaseFileYupObject } from 'components/formik/FormikFileUpload';
import { FileValue } from 'components/wrappers/BaseFileUpload';
import { WorkSiteWorkLocation } from '../FinancialInfo';
import { isEditorContentEmpty } from 'utils/editorUtils';

import style from './AssignmentInfo.module.less';
import styles from '../FinancialInfo/FinancialInfo.module.less';

type Props = {
  companyId?: string;
  formRef: any;
  onChange: (formik: FormikProps<AssignmentInfoFormValues>) => void;
  isEmploymentTypeVisible?: boolean;
  initialValues?: AssignmentInfoFormValues;
  assignmentStatus?: AssignmentStatusTypeEnum;
  hasRenewModeAndDateRestriction?: boolean;
  selectedCustomer?: CustomerResponseModel;
  hasRehireModeAndDateRestriction?: boolean;
  w2InfoList?: PriceAgreementW2InfoResponseModel[];
  workSiteWorkLocation: WorkSiteWorkLocation | null;
  isRenewOrRehireMode: boolean;
  updateAssignmentValuesWithFilteredMultiRates: (
    jobCategory: JobCategoryEnum | null,
  ) => void;
};

export interface AssignmentInfoFormValues {
  jobTitle: string;
  startDate: string | null;
  endDate: string | null;
  jobDescription: FormikTextEditorValue;
  jobCategory: JobCategoryEnum | null;
  employmentType: EmploymentTypeEnum | null;
  anticipateSchedule: AnticipatedScheduleTypeEnum | null;
  customerPurchaseOrder?: string;
  supportingDocuments: FileValue[];
  anticipateOtherData?: {
    hoursFrom: TimePointEnum | null;
    dayTimeFrom: TimeAMPMEnum;
    hoursTo: TimePointEnum | null;
    dayTimeTo: TimeAMPMEnum;
  };
  workFromHome?: WorkFromHomeEnum;
  preScreeningRequired: boolean;
  preScreeningBilledTo: PreScreeningBilledToEnum | null;
  specialInstructions?: string;
  internalReports: AssignmentTeammateSelectModel[];
  // workCode?: string;
}

const getInitialValidationSchema = (isCorporateTA: boolean) => {
  const initialSchema: any = {
    jobTitle: Yup.string()
      .trim()
      .max(120, 'Job title should be 120 characters maximum'),
    startDate: Yup.date().nullable(),
    endDate: Yup.date()
      .nullable()
      .when('startDate', {
        is: (startDate?: Date | null) => !!startDate,
        then: Yup.date()
          .nullable()
          .test(
            'isEndDateLessThanStartDate',
            'End date cannot be less than start date',
            function (endDate?: Date | null) {
              if (!endDate) return true;

              return validateNoEndDateLessStartDate(
                this.parent.startDate,
                endDate,
              );
            },
          ),
      }),
    jobDescription: Yup.object({
      content: Yup.string(),
    }),
    anticipateSchedule: Yup.number().nullable(),
    jobCategory: Yup.number().nullable(),
    workFromHome: Yup.number(),
    preScreeningRequired: Yup.boolean(),
    anticipateOtherData: Yup.object({}),
    supportingDocuments: Yup.array().of(BaseFileYupObject),
    preScreeningBilledTo: Yup.number()
      .nullable()
      .when('preScreeningRequired', {
        is: (preScreeningRequired: boolean) => preScreeningRequired,
        then: Yup.number().required('Field is required'),
      }),
    specialInstructions: Yup.string()
      .trim()
      .max(2000, 'Special instructions should be 2000 characters maximum'),
    internalReports: Yup.array().of(
      Yup.object(initialInternalReportValidationSchema),
    ),
    // workCode: Yup.string().max(25, 'Work code should be 25 characters maximum'),
  };

  if (!isCorporateTA) {
    initialSchema.customerPurchaseOrder = Yup.string()
      .trim()
      .max(120, 'Customer PO Number should be 120 characters maximum');
  }

  return initialSchema;
};

const getSubmitValidationSchema = (
  isEmploymentTypeVisible: boolean,
  isCorporateTA: boolean,
) => {
  const initialSchema = getInitialValidationSchema(isCorporateTA);
  const schema: any = {
    ...initialSchema,
    jobTitle: initialSchema.jobTitle.required('Field is required'),
    startDate: initialSchema.startDate.required('Field is required'),
    endDate: initialSchema.endDate.required('Field is required'),
    jobDescription: initialSchema.jobDescription.test(
      'jobDescription',
      function (this: Yup.TestContext, value: FormikTextEditorValue) {
        if (!value || !value.content || isEditorContentEmpty(value.content)) {
          return this.createError({
            message: 'Field is required',
          });
        }
        if (value.content.length < 100) {
          return this.createError({
            message: 'Job description should be 100 characters minimum',
          });
        }
        if (value.content.length > 10000) {
          return this.createError({
            message: 'Job description should be 10000 characters maximum',
          });
        }
        return true;
      },
    ),
    anticipateSchedule:
      initialSchema.anticipateSchedule.required('Field is required'),
    jobCategory: initialSchema.jobCategory.required('Field is required'),
    workFromHome: initialSchema.workFromHome.required('Field is required'),
    anticipateOtherData: initialSchema.anticipateOtherData.when(
      'anticipateSchedule',
      {
        is: (anticipateSchedule: number) =>
          anticipateSchedule === AnticipatedScheduleTypeEnum.Other,
        then: Yup.object({
          hoursFrom: Yup.string().nullable().required('Field is required'),
          dayTimeFrom: Yup.number().required('Field is required'),
          hoursTo: Yup.string().nullable().required('Field is required'),
          dayTimeTo: Yup.number().required('Field is required'),
        }),
      },
    ),
    internalReports: Yup.array().of(
      Yup.object(submitInternalReportValidationSchema),
    ),
    // workCode: initialSchema.workCode.required('Field is required'),
  };

  if (isEmploymentTypeVisible) {
    schema.employmentType = Yup.number()
      .nullable()
      .required('Field is required');
  }

  return Yup.object(schema);
};

export default React.memo((props: Props) => {
  const {
    companyId,
    formRef,
    onChange,
    isEmploymentTypeVisible = true,
    initialValues: predefinedInitialValues,
    assignmentStatus,
    hasRenewModeAndDateRestriction,
    selectedCustomer,
    hasRehireModeAndDateRestriction,
    isRenewOrRehireMode,
    updateAssignmentValuesWithFilteredMultiRates,
    // w2InfoList,
    // workSiteWorkLocation,
  } = props;
  const dispatch = useAppDispatch();
  const jobTitles = useAppSelector(jobTitlesSelector);
  const isJobTitlesLoading = useAppSelector(isJobTitlesLoadingSelector);
  const isCorporateTA = useAppSelector(isCorporateTASelector);
  const isAdmin = useAppSelector(isAdminSelector);
  const isActiveStatus = assignmentStatus === AssignmentStatusTypeEnum.Active;
  // const [hasRendered, setHasRendered] = useState(false);

  // const getUpdatedWorkerCodes = useCallback(() => {
  //   const workState = workSiteWorkLocation?.state;
  //
  //   const listOfAllWorkerCodes = (w2InfoList || [])
  //     .flatMap((info) => info.workCodes)
  //     .filter((code): code is string => Boolean(code));
  //
  //   const w2InfoStateIds = (w2InfoList || []).map((w2Info) => ({
  //     ...w2Info,
  //     placementStates: (w2Info.placementStates || []).map((state) => state.id),
  //   }));
  //
  //   const workStateMatched = w2InfoStateIds.find((info) =>
  //     info.placementStates.includes(workState),
  //   );
  //
  //   return workStateMatched?.workCodes?.length
  //     ? workStateMatched.workCodes
  //     : listOfAllWorkerCodes;
  // }, [w2InfoList, workSiteWorkLocation?.state]);

  // const [workerCodesList, setWorkerCodesList] = useState<string[]>(
  //   getUpdatedWorkerCodes(),
  // );

  // useDidUpdate(() => {
  //   const result = getUpdatedWorkerCodes();
  //   setWorkerCodesList(result);
  // }, [workSiteWorkLocation?.state, w2InfoList, getUpdatedWorkerCodes]);

  // const resetWorkCodeField = () => {
  //   const formik = formRef.current;
  //   if (!formik) return;
  //
  //   formik.setFieldValue('workCode', undefined, false);
  //   formik.setFieldTouched('workCode', false);
  // };
  //
  // useDidUpdate(() => {
  //   if (hasRendered && !isNil(workSiteWorkLocation)) {
  //     resetWorkCodeField();
  //   }
  //   if (!hasRendered) {
  //     setHasRendered(true);
  //   }
  // }, [workSiteWorkLocation?.state]);

  const breakInServiceValue = getCustomerBreakInServiceValue(
    selectedCustomer?.breakInService,
  );

  const supportingDocumentsFiles: ResourceDownloadResponse[] = useMemo(
    () =>
      (predefinedInitialValues?.supportingDocuments || []).map((file) => ({
        fileName: file.fileName || '',
        fileKey: file.s3Key || '',
        downloadUrl: '',
      })),
    [predefinedInitialValues?.supportingDocuments],
  );

  const checkIsDisabledDate = () => {
    return !isAdmin && isActiveStatus;
  };

  const getInitialValues = (): AssignmentInfoFormValues => {
    const initialValues = {
      jobTitle: '',
      startDate: null,
      endDate: null,
      jobDescription: {
        content: '',
      },
      jobCategory: null,
      employmentType: null,
      anticipateSchedule: null,
      supportingDocuments: [],
      anticipateOtherData: {
        hoursFrom: null,
        dayTimeFrom: TimeAMPMEnum.AM,
        hoursTo: null,
        dayTimeTo: TimeAMPMEnum.PM,
      },
      workFromHome: undefined,
      preScreeningRequired: false,
      preScreeningBilledTo: null,
      specialInstructions: '',
      internalReports: [
        {
          userId: '',
          rate: undefined,
          teammateType: AssignmentTeammateTypeEnum.Teammate,
          externalFullName: undefined,
          rateType: AssignmentRateTypeEnum.Percent,
        },
      ],
      // workCode: undefined,
    };
    if (!isCorporateTA) {
      (initialValues as AssignmentInfoFormValues).customerPurchaseOrder = '';
    }
    return initialValues;
  };

  const checkIsEndDateHintDisplay = useCallback(
    (currentEndDate: string | null) => {
      if (
        assignmentStatus === AssignmentStatusTypeEnum.Active ||
        (assignmentStatus === AssignmentStatusTypeEnum.Request &&
          !isRenewOrRehireMode)
      ) {
        const endDate = moment(predefinedInitialValues?.endDate);
        const selected = moment(currentEndDate);

        return selected.isBefore(endDate);
      }
    },
    [predefinedInitialValues?.endDate, assignmentStatus, isRenewOrRehireMode],
  );

  const submitValidationSchema = useMemo(
    () =>
      getSubmitValidationSchema(
        isEmploymentTypeVisible,
        isCorporateTA as boolean,
      ),
    [isEmploymentTypeVisible, isCorporateTA],
  );

  useEffect(() => {
    dispatch(getJobTitles());
    // dispatch(getAllWorkCodes(isAdmin));
    // eslint-disable-next-line
  }, [dispatch]);

  const handleAnticipateScheduleChange = (
    value: AnticipatedScheduleTypeEnum,
    formik: FormikProps<AssignmentInfoFormValues>,
  ) => {
    if (value !== AnticipatedScheduleTypeEnum.Other) return;

    formik.setFieldValue(
      'anticipateOtherData',
      {
        hoursFrom: null,
        dayTimeFrom: TimeAMPMEnum.AM,
        hoursTo: null,
        dayTimeTo: TimeAMPMEnum.PM,
      },
      false,
    );
    formik.setFieldTouched('anticipateOtherData', false);
  };

  const handlePreScreeningRequiredChange = (
    e: any,
    formik: FormikProps<AssignmentInfoFormValues>,
  ) => {
    if (!e.target.value) {
      formik.setFieldValue('preScreeningBilledTo', null, false);
    } else {
      formik.setFieldValue(
        'preScreeningBilledTo',
        PreScreeningBilledToEnum.Member,
      );
      formik.setFieldTouched('preScreeningBilledTo', false, false);
    }
  };

  const disableDatesBasedOnMode = (current: any, startDate: string | null) => {
    return hasRenewModeAndDateRestriction
      ? disableDatesBeforeSpecificDate(
          getLaterDate(
            startDate,
            breakInServiceValue ? breakInServiceValue + 1 : undefined,
          ),
        )(current)
      : disableDatesBeforeSpecificDate(startDate)(current);
  };

  return (
    <Formik
      innerRef={formRef}
      enableReinitialize
      initialValues={predefinedInitialValues || getInitialValues()}
      validationSchema={submitValidationSchema}
      onSubmit={() => {}}
    >
      {(formik) => (
        <>
          <FormikEffect onChange={onChange} />
          <Form layout="vertical">
            <div className="modal-scroll-wrapper">
              {checkIsEndDateHintDisplay(formik.values.endDate) && (
                <div className={styles.hint}>
                  <i className="icon icon-info-filled" />
                  Changing the assignment end date won't terminate it. If you
                  want to terminate the assignment, please use the termination
                  option on the Assignment page.
                </div>
              )}
              <div className="modal-title">Assignment information</div>

              <Row gutter={40}>
                <Col xs={24} md={12}>
                  <FormItem label="Job title *" name="jobTitle">
                    <FormikAutoCompleteWithLoading
                      isLoading={isJobTitlesLoading}
                      name="jobTitle"
                      filterOption
                      placeholder="Enter job title"
                      showSearch
                    >
                      {jobTitles.map((jobTitle) => (
                        <FormikAutoCompleteWithLoading.Option
                          key={`assignment-jobTitle-${jobTitle.id}`}
                          value={jobTitle.title || ''}
                        >
                          {jobTitle.title}
                        </FormikAutoCompleteWithLoading.Option>
                      ))}
                    </FormikAutoCompleteWithLoading>
                  </FormItem>
                </Col>

                <Col xs={24} md={12}>
                  <Row gutter={20}>
                    <Col xs={24} md={12}>
                      <FormItem label="Start date *" name="startDate">
                        <DatePicker
                          name="startDate"
                          placeholder="Select start date"
                          showToday={false}
                          format="MM/DD/YYYY"
                          className="d-flex"
                          disabledDate={
                            hasRenewModeAndDateRestriction ||
                            hasRehireModeAndDateRestriction
                              ? disableDatesBeforeSpecificDate(
                                  hasRehireModeAndDateRestriction
                                    ? moment.utc()
                                    : predefinedInitialValues?.startDate,
                                )
                              : undefined
                          }
                          disabled={checkIsDisabledDate()}
                        />
                      </FormItem>
                    </Col>
                    <Col xs={24} md={12}>
                      <FormItem label="End date (expected) *" name="endDate">
                        <DatePicker
                          key={
                            `${formik.values.startDate}-${breakInServiceValue}` ||
                            'endDate'
                          }
                          name="endDate"
                          placeholder="Select end date"
                          disabledDate={(current) =>
                            disableDatesBasedOnMode(
                              current,
                              formik.values.startDate,
                            )
                          }
                          disabled={checkIsDisabledDate()}
                          showToday={false}
                          format="MM/DD/YYYY"
                          className="d-flex"
                        />
                      </FormItem>
                    </Col>
                  </Row>
                </Col>

                <Col xs={24} md={12}>
                  <Row gutter={20}>
                    <Col xs={24}>
                      <FormItem
                        label="Job description *"
                        name="jobDescription"
                        className={style.jobDescription}
                      >
                        <FormikTextEditor
                          name="jobDescription"
                          placeholder="Enter the full job description, including duties, responsibilities, required skills, work environment, schedule, and any physical requirements."
                        />
                      </FormItem>

                      <FormItem name="workFromHome" label="Working location *">
                        <Select
                          name="workFromHome"
                          options={getWorkFromHomeOptions()}
                          placeholder="Select working location"
                        >
                          {getWorkFromHomeOptions().map((option) => (
                            <Select.Option
                              key={`select-work-from-home-${option.value}`}
                              value={option.value}
                            >
                              {option.value}
                            </Select.Option>
                          ))}
                        </Select>
                      </FormItem>

                      <FormItem
                        className={cx('inline-radio', style.radio)}
                        label="Pre-screening required?"
                        name="preScreeningRequired"
                      >
                        <Radio.Group
                          className="radio"
                          name="preScreeningRequired"
                          onChange={(e) =>
                            handlePreScreeningRequiredChange(e, formik)
                          }
                          options={[
                            { label: 'Yes', value: true },
                            { label: 'No', value: false },
                          ]}
                        />
                      </FormItem>
                      <FormItem
                        className={cx('inline-radio', style.radio)}
                        label="Pre-screening billed to"
                        name="preScreeningBilledTo"
                      >
                        <Radio.Group
                          className="radio"
                          name="preScreeningBilledTo"
                          disabled={!formik.values.preScreeningRequired}
                          options={getPreScreeningBilledToOptions()}
                        />
                      </FormItem>

                      <InternalReportFieldArray companyId={companyId} />
                    </Col>
                  </Row>
                </Col>

                <Col xs={24} md={12}>
                  <FormItem name="jobCategory" label="Job category *">
                    <Select
                      name="jobCategory"
                      placeholder="Select job category"
                      onChange={(value) =>
                        updateAssignmentValuesWithFilteredMultiRates(value)
                      }
                    >
                      {getEmploymentCategoryOptions().map((category) => (
                        <Select.Option
                          key={`select-jobCategory-${category.value}`}
                          value={category.value}
                        >
                          {category.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </FormItem>

                  {isEmploymentTypeVisible && (
                    <FormItem name="employmentType" label="Employment type *">
                      <Select
                        name="employmentType"
                        placeholder="Select employment type"
                      >
                        {getEmploymentTypeOptions().map((type) => (
                          <Select.Option
                            key={`select-employmentType-${type.value}`}
                            value={type.value}
                          >
                            {type.label}
                          </Select.Option>
                        ))}
                      </Select>
                    </FormItem>
                  )}

                  <FormItem
                    name="anticipateSchedule"
                    label="Anticipated schedule *"
                  >
                    <Select
                      name="anticipateSchedule"
                      placeholder="Select anticipated schedule"
                      onChange={(v) =>
                        handleAnticipateScheduleChange(v, formik)
                      }
                    >
                      {getAnticipateScheduleOptions().map((schedule) => (
                        <Select.Option
                          key={`select-anticipateSchedule-${schedule.value}`}
                          value={schedule.value}
                        >
                          {schedule.label}
                        </Select.Option>
                      ))}
                    </Select>
                  </FormItem>

                  {/*<FormItem name="workCode" label="WC Code *">
                    <FormikAutoCompleteWithLoading
                      name="workCode"
                      filterOption
                      placeholder="Select worker code"
                    >
                      {workerCodesList.map((code) => (
                        <FormikAutoCompleteWithLoading.Option
                          key={`select-worker-code-${code}`}
                          value={code}
                        >
                          {code}
                        </FormikAutoCompleteWithLoading.Option>
                      ))}
                    </FormikAutoCompleteWithLoading>
                  </FormItem>*/}

                  {!isCorporateTA && (
                    <FormItem
                      name="customerPurchaseOrder"
                      label="Customer PO Number"
                    >
                      <Input
                        name="customerPurchaseOrder"
                        placeholder="Enter customer PO number"
                        autoComplete="off"
                      />
                    </FormItem>
                  )}

                  <h5 className="modal-title">
                    Please upload supporting Assignment files
                  </h5>
                  <div className="upload-wrapper">
                    <FormItem
                      name="supportingDocuments"
                      showValidateError={false}
                    >
                      <SupportDocumentUploader
                        fieldName="supportingDocuments"
                        files={supportingDocumentsFiles}
                        isAdmin={isAdmin}
                      />
                    </FormItem>
                  </div>

                  <FormItem
                    label="Special instructions"
                    name="specialInstructions"
                    className={style.textArea}
                  >
                    <Input.TextArea
                      name="specialInstructions"
                      placeholder="Enter special instructions"
                      autoComplete="off"
                    />
                  </FormItem>
                </Col>
              </Row>

              {formik.values.anticipateSchedule ===
                AnticipatedScheduleTypeEnum.Other && (
                <Row gutter={40}>
                  <Col xs={24} md={12} className={style.anticipateSchedule}>
                    <Row gutter={[{ xs: 10, sm: 10, md: 20 }, 0]}>
                      <Col xs={12}>
                        <FormItem
                          name="anticipateOtherData.hoursFrom"
                          label="From *"
                        >
                          <Select
                            name="anticipateOtherData.hoursFrom"
                            placeholder="Select time"
                          >
                            {getAnticipateOtherHoursOptions().map((hours) => (
                              <Select.Option
                                key={`select-anticipateOtherData-hoursFrom-${hours.value}`}
                                value={hours.value}
                              >
                                {hours.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </FormItem>
                      </Col>

                      <Col xs={12}>
                        <FormItem
                          name="anticipateOtherData.dayTimeFrom"
                          label=" "
                        >
                          <Select name="anticipateOtherData.dayTimeFrom">
                            {getDayTimeOptions().map((dayTime) => (
                              <Select.Option
                                key={`select-anticipateOtherData-dayTimeFrom-${dayTime.value}`}
                                value={dayTime.value}
                              >
                                {dayTime.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </FormItem>
                      </Col>
                    </Row>
                  </Col>

                  <Col xs={24} md={12}>
                    <Row gutter={[{ xs: 10, sm: 10, md: 20 }, 0]}>
                      <Col xs={12}>
                        <FormItem
                          name="anticipateOtherData.hoursTo"
                          label="To *"
                        >
                          <Select
                            name="anticipateOtherData.hoursTo"
                            placeholder="Select time"
                          >
                            {getAnticipateOtherHoursOptions().map((hours) => (
                              <Select.Option
                                key={`select-anticipateOtherData-hoursTo-${hours.value}`}
                                value={hours.value}
                              >
                                {hours.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </FormItem>
                      </Col>

                      <Col xs={12}>
                        <FormItem
                          name="anticipateOtherData.dayTimeTo"
                          label=" "
                        >
                          <Select name="anticipateOtherData.dayTimeTo">
                            {getDayTimeOptions().map((dayTime) => (
                              <Select.Option
                                key={`select-anticipateOtherData-dayTimeTo-${dayTime.value}`}
                                value={dayTime.value}
                              >
                                {dayTime.label}
                              </Select.Option>
                            ))}
                          </Select>
                        </FormItem>
                      </Col>
                    </Row>
                  </Col>
                </Row>
              )}
            </div>
          </Form>
        </>
      )}
    </Formik>
  );
});
